import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/shared/api-client/workspace';
import { Button } from '@/shared/ui/components/button';
import { Textarea } from '@/shared/ui/components/textarea';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useMvpVisits, useUndoMvpVisit, type MvpVisit } from './holders';
import { moment, windowTime, type VisitFormat } from './visitFormat';

export function VisitsPanel({
  venueId,
  customerId,
  format,
}: {
  venueId: string;
  customerId: string;
  format: VisitFormat;
}) {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const visits = useMvpVisits(venueId, customerId, true);
  const undo = useUndoMvpVisit(venueId, customerId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const canUndo = auth.can('mvp.redemption.undo', { kind: 'venue', venueId });

  function submit(event: FormEvent, visit: MvpVisit) {
    event.preventDefault();
    if (!reason.trim()) return;
    undo.mutate(
      { visitId: visit.id, reason: reason.trim() },
      {
        onSuccess: () => {
          setEditingId(null);
          setReason('');
        },
      },
    );
  }

  if (visits.isPending)
    return <p className="text-sm text-muted-foreground">{t('mvpCustomers.visits.loading')}</p>;
  if (visits.isError)
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('mvpCustomers.visits.loadFailed')}
      </p>
    );
  if (visits.data.length === 0)
    return <p className="text-sm text-muted-foreground">{t('mvpCustomers.visits.empty')}</p>;

  return (
    <ol className="grid gap-3">
      {visits.data.map((visit) => {
        const stamp = visit.draws.find((draw) => draw.undoneAt);
        const error = undo.error;
        const refusal = error instanceof ApiError && error.code === 'mvp_visit_pass_unavailable';
        return (
          <li key={visit.id} className="rounded-lg border p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium">{moment(visit.enteredAt, format)}</p>
                <p className="text-sm text-muted-foreground">
                  {t('mvpCustomers.visits.guestsStay', {
                    guests: visit.guests,
                    hours: visit.stayHours,
                  })}
                  {' · '}
                  {t('mvpCustomers.visits.window', {
                    start: windowTime(visit.windowStartsAt, format),
                    end: windowTime(visit.windowEndsAt, format),
                  })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('mvpCustomers.visits.sale', {
                    receipt: visit.receiptNumber ?? t('mvpCustomers.visits.receiptUnavailable'),
                  })}
                  {' · '}
                  {t('mvpCustomers.visits.servedBy', {
                    server: visit.servedBy ?? t('mvpCustomers.visits.unknownOperator'),
                  })}
                </p>
              </div>
              {visit.undone ? (
                <span className="text-sm font-medium text-amber-700">
                  {t('mvpCustomers.visits.undone')}
                </span>
              ) : null}
            </div>
            {visit.guestWaivers.length > 0 ? (
              <ul
                aria-label={t('mvpCustomers.visits.guestWaivers')}
                className="mt-3 grid gap-2 text-sm"
              >
                {visit.guestWaivers.map((waiver) => (
                  <li key={waiver.waiverRecordId} className="grid gap-1">
                    <span className="font-medium">{waiver.participantName}</span>
                    <span className="text-xs text-muted-foreground">
                      {t('mvpCustomers.visits.signedBy', { name: waiver.signerName })}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            <ul className="mt-3 grid gap-1 text-sm">
              {visit.draws.map((draw) => (
                <li key={draw.multiVisitPassId}>
                  {t('mvpCustomers.visits.draw', { name: draw.optionName, hours: draw.hours })}
                </li>
              ))}
            </ul>
            {stamp?.undoneAt ? (
              <p className="mt-3 text-sm text-muted-foreground">
                {t('mvpCustomers.visits.undoStamp', {
                  time: moment(stamp.undoneAt, format),
                  reason: stamp.undoReason ?? '',
                  by: stamp.undoneBy ?? t('mvpCustomers.visits.unknownOperator'),
                })}
              </p>
            ) : null}
            {canUndo && !visit.undone ? (
              editingId === visit.id ? (
                <form className="mt-3 grid gap-2" onSubmit={(event) => submit(event, visit)}>
                  <label htmlFor={`undo-${visit.id}`} className="text-sm font-medium">
                    {t('mvpCustomers.visits.reason')}
                  </label>
                  <Textarea
                    id={`undo-${visit.id}`}
                    value={reason}
                    required
                    onChange={(event) => setReason(event.target.value)}
                  />
                  {undo.isError ? (
                    <p role="alert" className="text-sm text-destructive">
                      {refusal
                        ? t('mvpCustomers.visits.unavailable')
                        : t('mvpCustomers.visits.undoFailed')}
                    </p>
                  ) : null}
                  <div className="flex gap-2">
                    <Button type="submit" disabled={undo.isPending || !reason.trim()}>
                      {t('mvpCustomers.visits.confirmUndo')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setEditingId(null);
                        setReason('');
                        undo.reset();
                      }}
                    >
                      {t('mvpCustomers.visits.cancel')}
                    </Button>
                  </div>
                </form>
              ) : (
                <Button
                  className="mt-3"
                  variant="outline"
                  onClick={() => {
                    setEditingId(visit.id);
                    undo.reset();
                  }}
                >
                  {t('mvpCustomers.visits.undo')}
                </Button>
              )
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
